// ============================================
// Demographic Distribution Check
//
// Monitors the demographic spread of calibration
// cases to ensure the validation dataset is
// representative. Flags gaps per PI-007.
// ============================================

import type { CalibrationCase } from '../../types/labUpload';

interface DemographicDistribution {
  total: number;
  bySex: Record<string, number>;
  byAgeRange: Record<string, number>;
  womenCases35to60: number;
  warnings: string[];
}

const AGE_RANGES = [
  { label: '18-34', min: 18, max: 34 },
  { label: '35-60', min: 35, max: 60 },
  { label: '61+', min: 61, max: 200 },
  { label: 'unknown', min: -1, max: -1 },
];

/**
 * Compute demographic distribution of calibration cases.
 * Pure function — no API calls.
 */
export function computeDemographicDistribution(
  cases: CalibrationCase[],
): DemographicDistribution {
  const bySex: Record<string, number> = { Male: 0, Female: 0, unknown: 0 };
  const byAgeRange: Record<string, number> = {};
  let womenCases35to60 = 0;
  const warnings: string[] = [];

  for (const range of AGE_RANGES) {
    byAgeRange[range.label] = 0;
  }

  for (const c of cases) {
    const sex = c.demographics?.sex ?? 'unknown';
    bySex[sex] = (bySex[sex] || 0) + 1;

    const age = c.demographics?.age;
    if (age !== undefined && age !== null) {
      const range = AGE_RANGES.find((r) => age >= r.min && age <= r.max);
      if (range) {
        byAgeRange[range.label]++;
      }
    } else {
      byAgeRange['unknown']++;
    }

    // Track women 35-60 specifically for PI-007
    if (sex === 'Female' && age !== undefined && age >= 35 && age <= 60) {
      womenCases35to60++;
    }
  }

  // Generate warnings
  if (cases.length >= 25) {
    // Women 35-60 target: 20
    if (womenCases35to60 < 20) {
      warnings.push(
        `Only ${womenCases35to60} women age 35-60 (need 20 for launch gate)`,
      );
    }

    // Check for severe sex imbalance (>80% one sex)
    const totalKnownSex = bySex['Male'] + bySex['Female'];
    if (totalKnownSex > 0) {
      const femaleRatio = bySex['Female'] / totalKnownSex;
      if (femaleRatio > 0.8) {
        warnings.push(
          `${Math.round(femaleRatio * 100)}% female — consider adding male cases`,
        );
      } else if (femaleRatio < 0.2) {
        warnings.push(
          `Only ${Math.round(femaleRatio * 100)}% female — Dr. C's practice may skew male`,
        );
      }
    }

    // Check for missing demographics
    const unknownSex = bySex['unknown'] || 0;
    const unknownAge = byAgeRange['unknown'] || 0;
    if (unknownSex > cases.length * 0.1) {
      warnings.push(
        `${unknownSex} cases missing sex data (${Math.round(unknownSex / cases.length * 100)}%)`,
      );
    }
    if (unknownAge > cases.length * 0.1) {
      warnings.push(
        `${unknownAge} cases missing age data (${Math.round(unknownAge / cases.length * 100)}%)`,
      );
    }
  }

  return {
    total: cases.length,
    bySex,
    byAgeRange,
    womenCases35to60,
    warnings,
  };
}
